import type { StylesConfig } from 'react-select-5';

interface BaseOption {
  label: string;
  value: string | number;
}

// AIDEV-NOTE: Shared dark-themed react-select styles for SPA components.
// Uses CSS custom properties (--spa-*) so it works in both dark and light SPA themes.
// Previously duplicated in AdvancedFilters.tsx and SectionPicker.tsx.
const darkSelectStyles: StylesConfig<BaseOption, false> = {
  clearIndicator: base => ({ ...base, color: 'var(--spa-text-secondary)', padding: '0.375rem' }),
  // AIDEV-NOTE: The coarse-pointer bump to 16px lives here, not in _sysadmin_spa.scss with the rest
  // of them: react-select puts an inline `font: inherit` on its real <input>, which outranks any
  // stylesheet, so the size has to be set on an ancestor it inherits from. Option matches so the
  // portaled menu does not read a size smaller than the control it opened from.
  control: (base, state) => ({
    ...base,
    '&:hover': { borderColor: 'var(--spa-accent)' },
    '@media (pointer: coarse)': { fontSize: '1rem' },
    backgroundColor: 'var(--spa-bg-secondary)',
    borderColor: state.isFocused ? 'var(--spa-accent)' : 'var(--spa-border)',
    boxShadow: state.isFocused ? '0 0 0 1px var(--spa-accent)' : 'none',
    fontSize: '0.875rem',
    minHeight: '2.375rem',
  }),
  dropdownIndicator: base => ({ ...base, color: 'var(--spa-text-secondary)', padding: '0.375rem' }),
  groupHeading: base => ({
    ...base,
    color: 'var(--spa-text-secondary)',
    fontSize: '0.75rem',
    fontWeight: 600,
    padding: '0.5rem 0.75rem 0.25rem',
    textTransform: 'none',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  input: base => ({ ...base, color: 'var(--spa-text-on-bg)' }),
  loadingMessage: base => ({ ...base, color: 'var(--spa-text-secondary)' }),
  menu: base => ({
    ...base,
    backgroundColor: 'var(--spa-bg-secondary)',
    border: '1px solid var(--spa-border)',
    marginTop: '0.25rem',
    overflow: 'hidden',
    zIndex: 50,
  }),
  menuPortal: base => ({ ...base, zIndex: 1052 }),
  // AIDEV-NOTE: The menu is the scroll container, so the default OS scrollbar renders a light
  // track over the dark theme. Standard props win in Chrome 121+/Firefox; the -webkit- rules
  // cover older Chrome and Safari.
  menuList: base => ({
    ...base,
    '&::-webkit-scrollbar': { width: '0.5rem' },
    '&::-webkit-scrollbar-thumb': { background: 'var(--spa-scrollbar-thumb)', borderRadius: '0.25rem' },
    '&::-webkit-scrollbar-thumb:hover': { background: 'var(--spa-scrollbar-hover)' },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    maxHeight: '17.5rem',
    padding: 0,
    scrollbarColor: 'var(--spa-scrollbar-thumb) transparent',
    scrollbarWidth: 'thin',
  }),
  // Multi-select tags on the accent tint, like SPAPill's accent tone.
  multiValue: base => ({ ...base, backgroundColor: 'var(--spa-accent-bg)', borderRadius: '0.25rem' }),
  multiValueLabel: base => ({ ...base, color: 'var(--spa-accent-text)', fontSize: '0.8125rem' }),
  multiValueRemove: base => ({
    ...base,
    '&:hover': { backgroundColor: 'var(--spa-accent-bg-hover)', color: 'var(--spa-accent-text-hover)' },
    color: 'var(--spa-accent-text)',
  }),
  noOptionsMessage: base => ({ ...base, color: 'var(--spa-text-secondary)' }),
  option: (base, state) => ({
    ...base,
    // Secondary lines in a custom option label (an email, a district) carry their own grey, which
    // vanishes on the highlight fill; on the highlighted row everything takes the row's colour.
    ...(state.isFocused && { '& *': { color: 'inherit' } }),
    '&:active': { backgroundColor: 'var(--spa-accent-solid)' },
    '@media (pointer: coarse)': { fontSize: '1rem' },
    backgroundColor: state.isFocused ? 'var(--spa-accent-solid)' : 'transparent',
    color: state.isFocused ? '#fff' : 'var(--spa-text-on-bg)',
    cursor: 'pointer',
    fontSize: '0.875rem',
    lineHeight: 1.4,
    whiteSpace: 'normal',
  }),
  placeholder: base => ({ ...base, color: 'var(--spa-text-secondary)' }),
  // Wraps to match the menu options — react-select's base nowrap clipped the edition/state
  // suffix, which is exactly what distinguishes one program from another.
  singleValue: base => ({
    ...base,
    color: 'var(--spa-text-on-bg)',
    lineHeight: 1.4,
    overflow: 'visible',
    textOverflow: 'clip',
    whiteSpace: 'normal',
  }),
  valueContainer: base => ({ ...base, padding: '0.25rem 0.5rem' }),
};

export default darkSelectStyles;
